import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { MenuItem } from '../services/authService';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  organizationId?: string;
  roles?: string[]; // User roles (e.g., ['Admin', 'Manager'])
  orgRole?: string; // Organization role (e.g., 'admin', 'member')
  permissions?: string[]; // User permissions (e.g., ['users.create', 'items.read'])
  menu?: MenuItem[]; // Dynamic menu based on permissions
  imageUrl?: string;
  /** Current browser session (from /auth/me); used for active-sessions UI */
  sessionId?: string | null;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setAuth: (user: User, accessToken: string, refreshToken: string) => void;
  clearAuth: () => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  updateUser: (user: Partial<User>) => void;

  // Permission helpers
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasRole: (role: string) => boolean;
  isAdmin: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, _get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      setAuth: (user, accessToken, refreshToken) =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        }),

      clearAuth: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        }),

      setLoading: (loading) => set({ isLoading: loading }),

      updateUser: (userData) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        })),

      // Permission helpers
      hasPermission: (permission) => {
        const state = useAuthStore.getState();
        return state.user?.permissions?.includes(permission) ?? false;
      },

      hasAnyPermission: (permissions) => {
        const state = useAuthStore.getState();
        const userPermissions = state.user?.permissions ?? [];
        return permissions.some((perm) => userPermissions.includes(perm));
      },

      hasAllPermissions: (permissions) => {
        const state = useAuthStore.getState();
        const userPermissions = state.user?.permissions ?? [];
        return permissions.every((perm) => userPermissions.includes(perm));
      },

      hasRole: (role) => {
        const state = useAuthStore.getState();
        return state.user?.roles?.includes(role) ?? false;
      },

      isAdmin: () => {
        const state = useAuthStore.getState();
        return state.user?.roles?.includes('Admin') ?? false;
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state: AuthState) => ({
        refreshToken: state.refreshToken,
        user: state.user,
        accessToken: state.accessToken,
      }), // Persist refresh token, user, and access token
      onRehydrateStorage: () => (state: AuthState | undefined) => {
        // After rehydration, set isAuthenticated based on whether we have user and refreshToken
        if (state?.user && state.refreshToken) {
          state.isAuthenticated = true;
        }
      },
    },
  ),
);
